/*
  File: OpsBoardPage.tsx
  Path: src/features/dashboard/pages/OpsBoardPage.tsx
  Description:
    P10 dashboard Ops Board home surface.
    Replaces legacy Overview as the operator's authenticated home page.
    Read-first surface. Displays operational truth and routes actions elsewhere.

  Author: Larry McLean + ChatGPT
  Created: 2026-06-20
  Version: 0.6.0
  Status: Active

  Change Log:
    - 2026-06-20 - v0.1.0:
      - Initial shell-safe Ops Board page scaffold.
      - Uses authenticated /api/v1/ops-board endpoint.
    - 2026-08-25 - v0.2.0:
      - Wire internal dive, course, and canonical day Manifest workbook generation.
      - Add explicit CDWS Egypt, provisional Aqaba, and AQUORIX stock selection.
    - 2026-08-25 - v0.3.0:
      - Preview the canonical official Manifest grid before download.
      - Replace profile blocks with a compact format selector.
      - Separate official format status from participant readiness.
      - Correct course remaining-capacity presentation.
    - 2026-08-26 - v0.4.0:
      - Add an inline participant correction panel to the Manifest grid.
      - Save governed missing fields through the operator-scoped endpoint.
      - Reload canonical readiness after each successful correction.
    - 2026-08-26 - v0.4.1:
      - Dock the correction editor outside the horizontally scrolling grid.
      - Keep participant identity and all correction commands visible.
*/

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./OpsBoardPage.css";
import { apiFetch } from "../../../utils/api";
import {
  generateScopedManifestFile,
  ManifestFileScope,
  ManifestProfileCode,
  NATIONALITIES,
  saveIdentity
} from "./useManifestData";


type OpsBoardSession = {
  id: string;
  title: string;
  start_time: string;
  meet_time?: string | null;
  meet_location?: string | null;
  site_name?: string | null;
  session_type?: string | null;
  dive_category?: string | null;
  capacity_total?: number | null;
  capacity_consumed?: number | null;
  capacity_remaining?: number | null;
  metadata?: {
    vessel_name?: string | null;
    lead_guide_id?: number | string | null;
    lead_guide_name?: string | null;
    dive_category?: string | null;
  };
};

type OpsBoardRosterPerson = {
  name?: string;
  participant_id?: number | null;
  guest_id?: number | null;
  cert?: string | null;
  gas?: string | null;
  currency?: string | null;
  line_total_minor?: number | null;
  certification_number?: string | null;
  certification_status?: string | null;
  medical_status?: string | null;
  readiness_note?: string | null;
  customer_status?: string | null;
  needs_rental_gear?: boolean;
  owns_full_gear?: boolean;
  dive_computer_needed?: boolean;
  nitrox_cert_verified?: boolean;
  payment_state?: string | null;
  forms_state?: string | null;
  readiness?: string | null;
  is_minor?: boolean;
  is_returning?: boolean;
  completion_id?: number | null;
  completion_status?: string | null;
  completion_attested_by?: number | null;
  completion_attested_at?: string | null;
  completion_acknowledged?: boolean;
  completion_ack_method?: string | null;
  completion_acknowledged_at?: string | null;
  completion_acknowledgment_sent?: boolean;
  has_note?: boolean;
  has_flag?: boolean;
  flag_reason?: string | null;
};

type PeekState = {
  person: OpsBoardRosterPerson;
} | null;

type CloseDiveState = {
  session: OpsBoardSession;
  roster: OpsBoardRosterPerson[];
  completedParticipantIds: number[];
  isSubmitting: boolean;
  error: string | null;
} | null;

type ManifestPreviewState = {
  mode: "day" | "session";
  title: string;
  subtitle: string;
  scope: ManifestFileScope;
} | null;

type ManifestGridRow = {
  participant_id?: number | null;
  first?: string | null;
  last?: string | null;
  dob?: string | null;
  nationality?: string | null;
  gender?: string | null;
  passport?: string | null;
  activity?: string | null;
  accommodation?: string | null;
  roomNumber?: string | null;
  isMinor?: boolean;
  guardianPassport?: string | null;
  guardianNationality?: string | null;
  missing?: string[];
};

type ManifestGridData = {
  title?: string;
  roster?: ManifestGridRow[];
  readiness?: {
    total?: number;
    complete?: number;
    blocking?: number;
  };
};

type ManifestCorrectionField = {
  field: string;
  label: string;
  type: "text" | "date" | "nationality" | "gender";
};

type ManifestCorrectionDraft = Record<string, string>;

type ManifestViewMode = "preview" | "edit";

const MANIFEST_CORRECTION_FIELDS: ManifestCorrectionField[] = [
  { field: "dob", label: "Birthdate", type: "date" },
  {
    field: "nationality",
    label: "Nationality",
    type: "nationality"
  },
  { field: "gender", label: "Gender", type: "gender" },
  {
    field: "passport",
    label: "Passport number",
    type: "text"
  },
  {
    field: "accommodation",
    label: "Accommodation",
    type: "text"
  },
  {
    field: "roomNumber",
    label: "Room number",
    type: "text"
  }
];

const MANIFEST_GUARDIAN_CORRECTION_FIELDS:
  ManifestCorrectionField[] = [
    {
      field: "guardianPassport",
      label: "Responsible passport number",
      type: "text"
    },
    {
      field: "guardianNationality",
      label: "Responsible nationality",
      type: "nationality"
    }
  ];

function manifestValueMissing(value: unknown): boolean {
  return (
    value === null
    || value === undefined
    || String(value).trim() === ""
  );
}

function correctionFieldsForPerson(
  person: ManifestGridRow
): ManifestCorrectionField[] {
  const fields = person.isMinor
    ? [
        ...MANIFEST_CORRECTION_FIELDS,
        ...MANIFEST_GUARDIAN_CORRECTION_FIELDS
      ]
    : MANIFEST_CORRECTION_FIELDS;

  return fields.filter((field) =>
    manifestValueMissing(
      person[field.field as keyof ManifestGridRow]
    )
  );
}

const MANIFEST_PROFILES: Array<{
  code: ManifestProfileCode;
  shortLabel: string;
  title: string;
  description: string;
  authorityReady: boolean;
}> = [
  {
    code: "CDWS_EG",
    shortLabel: "Egypt",
    title: "Egypt · CDWS",
    description: "Official CDWS GuestList workbook.",
    authorityReady: true
  },
  {
    code: "ASEZA_JO_PROVISIONAL",
    shortLabel: "Aqaba",
    title: "Aqaba · Provisional",
    description: "Viking workbook using governed generic labels.",
    authorityReady: false
  },
  {
    code: "AQX_STOCK_V1",
    shortLabel: "Stock",
    title: "AQUORIX · Stock",
    description: "Location-neutral Viking workbook.",
    authorityReady: false
  }
];

type OpsBoardSummary = {
  booking_count?: number;
  confirmed_pax?: number;
  active_hold_pax?: number;
  manual_review_count?: number;
  pickup_count?: number;
  nitrox_requested_count?: number;
  confirmed_revenue_minor?: number;
  to_collect_minor?: number;
  refunds_minor?: number;
  roster?: OpsBoardRosterPerson[];
  session_closed?: boolean;
  session_closed_at?: string | null;
  session_closed_by_user_id?: string | null;
  session_closed_by_name?: string | null;
};

type OpsBoardResponse = {
  ok: boolean;
  generated_at?: string;
  operator?: {
    name?: string;
    timezone?: string;
    currency?: string;
  };
  window?: {
    type?: string;
    start?: string;
    end?: string;
  };
  sessions?: OpsBoardSession[];
  session_summaries?: Record<string, OpsBoardSummary>;
  aggregates?: {
    total_sessions?: number;
    total_pax?: number;
    confirmed_pax_total?: number;
    active_hold_pax_total?: number;
    waitlist_pax_total?: number;
    nitrox_total?: number;
    rental_gear_total?: number;
    minor_session_count?: number;
    manual_review_total?: number;
    ready_total?: number;
    check_in_total?: number;
    attention_total?: number;
    pickup_total?: number;
    confirmed_revenue_total_minor?: number;
    to_collect_total_minor?: number;
    refunds_total_minor?: number;
  };
};

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: OpsBoardResponse };

function isValidYmd(value?: string | null): value is string {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

function addDaysToYmd(value: string, days: number): string {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function getTodayYmdForTimezone(timezone?: string | null): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone || "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(new Date());

  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

function getDiveCategoryLabel(value?: string | null): string | null {
  const raw = String(value || "").trim();
  return raw ? raw.toUpperCase() : null;
}

function getDiveCategorySlug(value?: string | null): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function getNumericSessionId(value?: string | number | null): string | null {
  const raw = String(value || "").trim();
  const match = raw.match(/(\d+)$/);
  return match ? match[1] : null;
}

function formatBoardDateLabel(value?: string | null) {
  if (!value) return "Today";

  const date = new Date(`${value}T12:00:00`);

  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function money(value: number, currency: string) {
  return `${currency} ${Number(value || 0).toFixed(2)}`;
}

function formatPaymentState(value?: string | null): string {
  if (value === "paid") return "Paid";
  if (value === "hold") return "Hold";
  if (value === "refunded") return "Refunded";
  return "Unpaid";
}

function formatFormsState(value?: string | null): string {
  if (value === "complete") return "✓ Complete";
  if (value === "sent_pending") return "Sent · pending";
  if (value === "medical_clearance_required") return "Medical · clearance req";
  if (value === "guardian_pending") return "Guardian · pending";
  if (value === "not_started") return "Forms pending";
  return "Forms pending";
}

function getPeekInitials(value?: string | null): string {
  const parts = String(value || "Diver")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  const first = parts[0]?.[0] || "D";
  const second = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";

  return `${first}${second}`.toUpperCase();
}

function getPeekReadinessClass(value?: string | null): string {
  if (value === "ready") return "ready";
  if (value === "attention") return "attn";
  return "check";
}

function getPeekReadinessLabel(value?: string | null): string {
  if (value === "ready") return "Ready";
  if (value === "attention") return "Needs attention";
  return "Needs check-in";
}

function getPeekReadinessIcon(value?: string | null) {
  if (value === "ready") return <ReadyIcon />;
  if (value === "attention") return <AttentionIcon />;
  return <CheckInIcon />;
}

function getPeekReassureText(person: OpsBoardRosterPerson): string {
  if (person.forms_state === "medical_clearance_required") {
    return "Doctor's clearance required before this diver can dive.";
  }

  if (person.payment_state !== "paid") {
    return "Almost there — collect on arrival.";
  }

  if (person.readiness === "ready") {
    return "Cleared and ready to dive.";
  }

  return "Almost there — complete check-in on arrival.";
}

function getPeekGasLabel(value?: string | null): string {
  if (value === "EAN") return "Nitrox (EAN)";
  if (value === "Tx") return "Trimix";
  return value || "Air";
}

function getPeekGearLabel(person: OpsBoardRosterPerson): string {
  if (person.needs_rental_gear === true) return "Rental";
  if (person.owns_full_gear === true) return "Own gear";
  return "Gear TBD";
}

function getPeekGearDetail(person: OpsBoardRosterPerson): string {
  if (person.dive_computer_needed === true) return "Computer needed";
  if (person.needs_rental_gear === true) return "Sizes pending";
  if (person.owns_full_gear === true) return "No rental";
  return "Confirm at check-in";
}

function getPeekGasDetail(person: OpsBoardRosterPerson): string {
  if (person.gas === "EAN" && person.nitrox_cert_verified === true) return "Cert verified";
  if (person.gas === "EAN") return "Verify cert";
  return "Standard gas";
}

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 8 12 3 3 8v8l9 5 9-5z" />
      <path d="M3 8l9 5 9-5" />
    </svg>
  );
}

function NoteIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

function MedicalIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M12 8.5v5M9.5 11h5" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function getPeekMoney(person: OpsBoardRosterPerson, fallbackCurrency: string): string {
  const personCurrency = person.currency || fallbackCurrency;
  const minor = Number(person.line_total_minor || 0);

  return money(minor / 100, personCurrency);
}

function PayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </svg>
  );
}

function GasIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="3" width="6" height="18" rx="3" />
      <path d="M9 8h6" />
    </svg>
  );
}

function CertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="5" />
      <path d="M8.5 12.5 7 21l5-2.5 5 2.5-1.5-8.5" />
    </svg>
  );
}

function FormsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function ReadyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function DidNotIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function CheckInIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5.5v8" />
      <path d="M12 18h.01" />
    </svg>
  );
}

function AttentionIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.3 4.2 1.9 18a2 2 0 0 0 1.7 3h16.8a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0z" />
      <path d="M12 10v3.5" />
      <path d="M12 17h.01" />
    </svg>
  );
}

export default function OpsBoardPage() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [peekState, setPeekState] = useState<PeekState>(null);
  const [manifestPreview, setManifestPreview] = useState<ManifestPreviewState>(null);
  const [manifestProfileCode, setManifestProfileCode] =
    useState<ManifestProfileCode>("CDWS_EG");
  const [manifestViewMode, setManifestViewMode] =
    useState<ManifestViewMode>("preview");
  const [manifestGenerating, setManifestGenerating] =
    useState(false);
  const [manifestError, setManifestError] =
    useState<string | null>(null);
  const [manifestGrid, setManifestGrid] =
    useState<ManifestGridData | null>(null);
  const [manifestGridLoading, setManifestGridLoading] =
    useState(false);
  const [
    manifestCorrectionParticipantId,
    setManifestCorrectionParticipantId
  ] = useState<number | null>(null);
  const [
    manifestCorrectionDraft,
    setManifestCorrectionDraft
  ] = useState<ManifestCorrectionDraft>({});
  const [
    manifestCorrectionSaving,
    setManifestCorrectionSaving
  ] = useState(false);
  const [
    manifestCorrectionError,
    setManifestCorrectionError
  ] = useState<string | null>(null);
  const [peekMode, setPeekMode] = useState<"summary" | "note" | "cert" | "medical" | "forms">("summary");
  const [peekNoteBody, setPeekNoteBody] = useState("");
  const [peekCertAgency, setPeekCertAgency] = useState("");
  const [peekCertLevel, setPeekCertLevel] = useState("");
  const [peekCertNumber, setPeekCertNumber] = useState("");
  const [peekCertStatus, setPeekCertStatus] = useState("not_recorded");
  const [peekMedicalStatus, setPeekMedicalStatus] = useState("not_collected");
  const [peekReadinessNote, setPeekReadinessNote] = useState("");
  const [peekCustomerStatus, setPeekCustomerStatus] = useState("active");
  const [peekLiabilitySigned, setPeekLiabilitySigned] = useState(false);
  const [peekMedicalSigned, setPeekMedicalSigned] = useState(false);
  const [peekGuardianSigned, setPeekGuardianSigned] = useState(false);
  const [peekSaving, setPeekSaving] = useState(false);
  const [peekMessage, setPeekMessage] = useState("");
  const [closeDiveState, setCloseDiveState] = useState<CloseDiveState>(null);
  const peekRef = useRef<HTMLDivElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  const requestedDate = searchParams.get("date");
  const selectedDate = isValidYmd(requestedDate) ? requestedDate : null;

  function openCustomerRecord(person: OpsBoardRosterPerson) {
    if (!person.guest_id) {
      setPeekMessage("This roster row is not linked to a customer record yet.");
      return;
    }

    setPeekState(null);
    setPeekMode("summary");
    nav(`/dashboard/customers/${encodeURIComponent(String(person.guest_id))}`);
  }

  function beginPeekMode(mode: "note" | "cert" | "medical" | "forms", person: OpsBoardRosterPerson) {
    setPeekMessage("");
    setPeekSaving(false);

    if (mode === "note") {
      setPeekNoteBody("");
    }

    if (mode === "cert") {
      const certParts = String(person.cert || "").split(" · ").map((part) => part.trim()).filter(Boolean);
      setPeekCertLevel(certParts[0] || "");
      setPeekCertAgency(certParts[1] || "");
      setPeekCertNumber(person.certification_number || "");
      setPeekCertStatus(person.certification_status || (person.cert ? "verified" : "not_recorded"));
    }

    if (mode === "medical") {
      setPeekMedicalStatus(person.medical_status || "not_collected");
      setPeekReadinessNote(person.readiness_note || "");
      setPeekCustomerStatus(person.customer_status || "active");
    }

    if (mode === "forms") {
      const complete = person.forms_state === "complete";
      setPeekLiabilitySigned(complete);
      setPeekMedicalSigned(complete);
      setPeekGuardianSigned(person.is_minor === true ? complete : false);
    }

    setPeekMode(mode);
  }

  async function refreshPeekPerson(guestId: number) {
    const path = selectedDate
      ? `/api/v1/ops-board?date=${encodeURIComponent(selectedDate)}`
      : "/api/v1/ops-board";

    const response = await apiFetch(path, { method: "GET" });
    const json = (await response.json().catch(() => ({}))) as OpsBoardResponse;

    if (!response.ok || json?.ok === false) {
      throw new Error(`Ops Board refresh failed: HTTP ${response.status}`);
    }

    setState({ status: "ok", data: json });

    const updatedPerson = (Array.isArray((json as any).sessions) ? (json as any).sessions : [])
      .flatMap((session: any) => Array.isArray(session.roster) ? session.roster : [])
      .find((person: any) => Number(person.guest_id) === Number(guestId));

    if (updatedPerson) {
      setPeekState({ person: updatedPerson });
    }
  }

  async function savePeekNote() {
    const guestId = Number(peekState?.person?.guest_id);
    const body = peekNoteBody.trim();

    if (!guestId || peekSaving) return;
    if (!body) {
      setPeekMessage("Note body is required");
      return;
    }

    setPeekSaving(true);
    setPeekMessage("");

    try {
      const res = await apiFetch(`/api/v1/dashboard/customers/${encodeURIComponent(String(guestId))}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body })
      });
      const json = await res.json();

      if (!res.ok || json.ok === false) {
        throw new Error(json.message || "Note could not be saved");
      }

      if (guestId) {
        await refreshPeekPerson(guestId);
      }
      setPeekMode("summary");
      setPeekNoteBody("");
    } catch (err: any) {
      setPeekMessage(err?.message || "Note could not be saved");
    } finally {
      setPeekSaving(false);
    }
  }

  async function savePeekCert() {
    const guestId = Number(peekState?.person?.guest_id);

    if (!guestId || peekSaving) return;

    setPeekSaving(true);
    setPeekMessage("");

    try {
      const res = await apiFetch(`/api/v1/dashboard/customers/${encodeURIComponent(String(guestId))}/operator-profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          certification_agency: peekCertAgency.trim() || null,
          certification_level: peekCertLevel.trim() || null,
          certification_number: peekCertNumber.trim() || null,
          certification_status: peekCertStatus
        })
      });
      const json = await res.json();

      if (!res.ok || json.ok === false) {
        throw new Error(json.message || "Certification could not be saved");
      }

      await refreshPeekPerson(guestId);
      setPeekMode("summary");
    } catch (err: any) {
      setPeekMessage(err?.message || "Certification could not be saved");
    } finally {
      setPeekSaving(false);
    }
  }

  async function savePeekMedical() {
    const guestId = Number(peekState?.person?.guest_id);

    if (!guestId || peekSaving) return;

    setPeekSaving(true);
    setPeekMessage("");

    try {
      const res = await apiFetch(`/api/v1/dashboard/customers/${encodeURIComponent(String(guestId))}/operator-profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          medical_status: peekMedicalStatus,
          readiness_note: peekReadinessNote.trim() || null,
          customer_status: peekCustomerStatus
        })
      });
      const json = await res.json();

      if (!res.ok || json.ok === false) {
        throw new Error(json.message || "Readiness could not be saved");
      }

      await refreshPeekPerson(guestId);
      setPeekMode("summary");
    } catch (err: any) {
      setPeekMessage(err?.message || "Readiness could not be saved");
    } finally {
      setPeekSaving(false);
    }
  }

  async function savePeekForms() {
    const guestId = Number(peekState?.person?.guest_id);
    const participantId = Number(peekState?.person?.participant_id);

    if (peekSaving) return;

    if (!participantId) {
      setPeekMessage("This diver row is missing a booking participant ID, so forms cannot be approved from the Ops Board yet.");
      return;
    }

    if (!peekLiabilitySigned || !peekMedicalSigned) {
      setPeekMessage("Liability and medical must both be marked signed.");
      return;
    }

    if (peekState?.person?.is_minor === true && !peekGuardianSigned) {
      setPeekMessage("Guardian signature is required for a minor.");
      return;
    }

    setPeekSaving(true);
    setPeekMessage("");

    try {
      const res = await apiFetch(`/api/v1/ops-board/participants/${encodeURIComponent(String(participantId))}/forms-clearance`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          liability_signed: peekLiabilitySigned,
          medical_signed: peekMedicalSigned,
          guardian_signed: peekGuardianSigned
        })
      });
      const json = await res.json();

      if (!res.ok || json.ok === false) {
        throw new Error(json.message || "Forms could not be approved");
      }

      await refreshPeekPerson(guestId);
      setPeekMode("summary");
    } catch (err: any) {
      setPeekMessage(err?.message || "Forms could not be approved");
    } finally {
      setPeekSaving(false);
    }
  }

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const path = selectedDate
          ? `/api/v1/ops-board?date=${encodeURIComponent(selectedDate)}`
          : "/api/v1/ops-board";

        const response = await apiFetch(path, { method: "GET" });
        const json = (await response.json().catch(() => ({}))) as OpsBoardResponse;

        if (!response.ok || json?.ok === false) {
          throw new Error(`Ops Board load failed: HTTP ${response.status}`);
        }

        if (mounted) {
          setState({ status: "ok", data: json });

          const loadedDate = json?.window?.start || "";
          if (isValidYmd(loadedDate) && loadedDate !== requestedDate) {
            setSearchParams({ date: loadedDate }, { replace: true });
          }
        }
      } catch (error: any) {
        if (mounted) setState({ status: "error", message: error?.message || "Load failed" });
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [requestedDate, selectedDate, setSearchParams]);

  const data = state.status === "ok" ? state.data : null;
  const sessions = useMemo(() => data?.sessions || [], [data]);
  const summaries = data?.session_summaries || {};
  const aggregates = data?.aggregates || {};
  const currency = data?.operator?.currency || "JOD";
  const boardDate = data?.window?.start || "";

  useEffect(() => {
    if (!peekState) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusableSelector = [
      "button",
      "[href]",
      "input",
      "select",
      "textarea",
      "[tabindex]:not([tabindex='-1'])"
    ].join(",");

    const focusShell = () => {
      const focusable = Array.from(
        peekRef.current?.querySelectorAll<HTMLElement>(focusableSelector) || []
      ).filter((element) => !element.hasAttribute("disabled"));

      const first = focusable[0];
      if (first) first.focus();
    };

    focusShell();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setPeekState(null);
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = Array.from(
        peekRef.current?.querySelectorAll<HTMLElement>(focusableSelector) || []
      ).filter((element) => !element.hasAttribute("disabled"));

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      lastFocusedRef.current?.focus();
    };
  }, [peekState]);

  const openPeek = (person: OpsBoardRosterPerson) => {
    lastFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPeekMode("summary");
    setPeekMessage("");
    setPeekState({ person });
  };

  const closePeek = () => {
    setPeekState(null);
    setPeekMode("summary");
    setPeekMessage("");
  };

    const openCloseDive = (session: OpsBoardSession, roster: OpsBoardRosterPerson[]) => {
    const participantIds = roster
      .map((person) => Number(person.participant_id))
      .filter((participantId) => Number.isInteger(participantId) && participantId > 0);

    setCloseDiveState({
      session,
      roster,
      completedParticipantIds: participantIds,
      isSubmitting: false,
      error: null
    });
  };

  const closeCloseDive = () => {
    if (closeDiveState?.isSubmitting) return;
    setCloseDiveState(null);
  };

  const toggleCloseDiveParticipant = (participantId: number) => {
    setCloseDiveState((current) => {
      if (!current || current.isSubmitting) return current;

      const isCompleted = current.completedParticipantIds.includes(participantId);

      return {
        ...current,
        completedParticipantIds: isCompleted
          ? current.completedParticipantIds.filter((id) => id !== participantId)
          : [...current.completedParticipantIds, participantId],
        error: null
      };
    });
  };

  const markCloseDiveAllDidNotDive = () => {
    setCloseDiveState((current) => {
      if (!current || current.isSubmitting) return current;

      return {
        ...current,
        completedParticipantIds: [],
        error: null
      };
    });
  };

    const submitCloseDive = async () => {
    if (!closeDiveState || closeDiveState.isSubmitting) return;

    setCloseDiveState({
      ...closeDiveState,
      isSubmitting: true,
      error: null
    });

    try {
      const numericSessionId = getNumericSessionId(closeDiveState.session.id);

      if (!numericSessionId) {
        throw new Error("Close dive failed: missing session id");
      }

      const response = await apiFetch(`/api/v1/ops-board/sessions/${encodeURIComponent(numericSessionId)}/close`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          completed_participant_ids: closeDiveState.completedParticipantIds
        })
      });

      if (!response.ok) {
        throw new Error(`Close dive failed: HTTP ${response.status}`);
      }

      setCloseDiveState(null);

      const path = selectedDate
        ? `/api/v1/ops-board?date=${encodeURIComponent(selectedDate)}`
        : "/api/v1/ops-board";

      const refreshed = await apiFetch(path, { method: "GET" });
      const json = (await refreshed.json().catch(() => ({}))) as OpsBoardResponse;

      if (!refreshed.ok || json?.ok === false) {
        throw new Error(`Ops Board refresh failed: HTTP ${refreshed.status}`);
      }

      setState({ status: "ok", data: json });
    } catch (error: any) {
      setCloseDiveState((current) => current
        ? {
            ...current,
            isSubmitting: false,
            error: error?.message || "Close dive failed"
          }
        : current
      );
    }
  };

  const operatorToday = getTodayYmdForTimezone(data?.operator?.timezone || "UTC");
  const isViewingToday = boardDate === operatorToday;

  const goToDate = (date: string) => {
    if (!isValidYmd(date)) return;
    setSearchParams({ date });
  };

  const goToPreviousDay = () => {
    if (!isValidYmd(boardDate)) return;
    goToDate(addDaysToYmd(boardDate, -1));
  };

  const goToNextDay = () => {
    if (!isValidYmd(boardDate)) return;
    goToDate(addDaysToYmd(boardDate, 1));
  };

  const goToToday = () => {
    goToDate(operatorToday);
  };

  const loadManifestGrid = async (
    scope: ManifestFileScope,
    profileCode: ManifestProfileCode
  ) => {
    setManifestGridLoading(true);
    setManifestGrid(null);
    setManifestError(null);

    try {
      const response = await apiFetch(
        `/api/v1/manifest?date=${encodeURIComponent(boardDate)}`
        + `&profile_code=${encodeURIComponent(profileCode)}`
      );

      const body = await response.json().catch(() => ({}));

      if (!response.ok || body?.ok === false) {
        throw new Error(
          body?.error || "Manifest preview could not be loaded."
        );
      }

      let selected: ManifestGridData | null = null;

      if ("date" in scope) {
        selected = body?.day || null;
      } else if ("courseRunId" in scope) {
        selected = Array.isArray(body?.courses)
          ? body.courses.find(
              (event: any) =>
                Number(event?.course_run_id)
                === Number(scope.courseRunId)
            ) || null
          : null;
      } else {
        selected = Array.isArray(body?.trips)
          ? body.trips.find(
              (event: any) =>
                Number(event?.session_id)
                === Number(scope.sessionId)
            ) || null
          : null;
      }

      if (!selected) {
        throw new Error(
          "The selected Manifest scope was not found."
        );
      }

      setManifestGrid(selected);
    } catch (error: any) {
      setManifestError(
        error?.message || "Manifest preview could not be loaded."
      );
    } finally {
      setManifestGridLoading(false);
    }
  };

  const openDayManifest = () => {
    const scope: ManifestFileScope = { date: boardDate };

    setManifestError(null);
    setManifestGenerating(false);
    setManifestPreview({
      mode: "day",
      title: "Day manifest",
      subtitle: `${formatBoardDateLabel(boardDate)} · All booked dives and courses · One row per identity`,
      scope
    });

    void loadManifestGrid(
      scope,
      manifestProfileCode
    );
  };

  const openSessionManifest = (
    session: OpsBoardSession,
    _roster: OpsBoardRosterPerson[],
    _summary: OpsBoardSummary,
    isCourseSession: boolean
  ) => {
    const inventoryId = Number(
      getNumericSessionId(session.id)
    );

    setManifestError(null);
    setManifestGenerating(false);

    if (!Number.isInteger(inventoryId) || inventoryId < 1) {
      setManifestPreview(null);
      return;
    }

    const scope: ManifestFileScope = isCourseSession
      ? { courseRunId: inventoryId }
      : { sessionId: inventoryId };

    setManifestPreview({
      mode: "session",
      title: isCourseSession
        ? "Course manifest"
        : "Dive manifest",
      subtitle: `${session.title || session.site_name || "Session"} · ${session.start_time || "--:--"} · ${formatBoardDateLabel(boardDate)}`,
      scope
    });

    void loadManifestGrid(
      scope,
      manifestProfileCode
    );
  };

  const beginManifestCorrection = (
    person: ManifestGridRow
  ) => {
    const participantId = Number(person.participant_id);

    if (!Number.isInteger(participantId) || participantId < 1) {
      setManifestCorrectionError(
        "This row cannot be corrected because its participant identity is missing."
      );
      return;
    }

    const draft: ManifestCorrectionDraft = {};

    correctionFieldsForPerson(person).forEach((field) => {
      draft[field.field] = "";
    });

    setManifestCorrectionParticipantId(participantId);
    setManifestCorrectionDraft(draft);
    setManifestCorrectionSaving(false);
    setManifestCorrectionError(null);
  };

  const cancelManifestCorrection = () => {
    if (manifestCorrectionSaving) return;

    setManifestCorrectionParticipantId(null);
    setManifestCorrectionDraft({});
    setManifestCorrectionError(null);
  };

  const saveManifestCorrection = async (
    person: ManifestGridRow
  ) => {
    const participantId = Number(person.participant_id);

    if (
      !manifestPreview
      || !Number.isInteger(participantId)
      || participantId < 1
      || manifestCorrectionSaving
    ) {
      return;
    }

    const changes = correctionFieldsForPerson(person)
      .map((field) => ({
        field: field.field,
        value: String(
          manifestCorrectionDraft[field.field] || ""
        ).trim()
      }))
      .filter((change) => change.value !== "");

    if (changes.length < 1) {
      setManifestCorrectionError(
        "Enter at least one missing value before saving."
      );
      return;
    }

    setManifestCorrectionSaving(true);
    setManifestCorrectionError(null);

    try {
      for (const change of changes) {
        await saveIdentity(
          participantId,
          change.field,
          change.value,
          manifestProfileCode
        );
      }

      setManifestCorrectionParticipantId(null);
      setManifestCorrectionDraft({});

      await loadManifestGrid(
        manifestPreview.scope,
        manifestProfileCode
      );
    } catch (error: any) {
      setManifestCorrectionError(
        error?.message || "Manifest correction could not be saved."
      );
    } finally {
      setManifestCorrectionSaving(false);
    }
  };

  const closeManifestPreview = () => {
    if (manifestGenerating || manifestCorrectionSaving) return;
    setManifestPreview(null);
    setManifestGrid(null);
    setManifestGridLoading(false);
    setManifestError(null);
    setManifestCorrectionParticipantId(null);
    setManifestCorrectionDraft({});
    setManifestCorrectionError(null);
    setManifestViewMode("preview");
  };

  const manifestCorrectionPerson =
    manifestGrid?.roster?.find(
      (person) =>
        Number(person.participant_id)
        === manifestCorrectionParticipantId
    ) || null;

  const generateManifestWorkbook = async () => {
    if (!manifestPreview || manifestGenerating) return;

    setManifestGenerating(true);
    setManifestError(null);

    try {
      const generated = await generateScopedManifestFile(
        manifestPreview.scope,
        manifestProfileCode
      );

      const url = URL.createObjectURL(generated.blob);
      const anchor = document.createElement("a");

      anchor.href = url;
      anchor.download = generated.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      setManifestError(
        error?.message || "Manifest workbook could not be generated."
      );
    } finally {
      setManifestGenerating(false);
    }
  };

  if (state.status === "loading") {
    return <div className="ob-page"><div className="ob-card">Loading Ops Board...</div></div>;
  }

  if (state.status === "error") {
    return <div className="ob-page"><div className="ob-card ob-error">{state.message}</div></div>;
  }

  return (
    <div className="ob-page">
      <section className="ob-crown">
        <div className="ob-crown-head">
          <div>
            <h1>Dive Operations</h1>
            <p>The day at a glance: divers, departures, readiness, and money.</p>
          </div>
          <div className="ob-crown-tools">
            <button className="ob-manifest-day" type="button" onClick={openDayManifest}>
              Generate day manifest
            </button>
            <div className="ob-sync">Live · {data?.generated_at || "now"}</div>
          </div>
        </div>

        <div className="ob-money-cards">
          <div className="ob-money-card ob-money-confirmed">
            <div className="ob-money-label">✓ Confirmed today</div>
            <div className="ob-money-value">{money((aggregates.confirmed_revenue_total_minor ?? 0) / 1000, currency)}</div>
            <div className="ob-money-sub">{aggregates.confirmed_pax_total ?? 0} confirmed divers</div>
          </div>

          <div className="ob-money-card ob-money-collect">
            <div className="ob-money-label">◷ To collect today</div>
            <div className="ob-money-value">{money((aggregates.to_collect_total_minor ?? 0) / 1000, currency)}</div>
            <div className="ob-money-sub">{aggregates.active_hold_pax_total ?? 0} on active hold</div>
          </div>

          <div className="ob-money-card ob-money-refunds">
            <div className="ob-money-label">↩ Refunds today</div>
            <div className="ob-money-value">{money((aggregates.refunds_total_minor ?? 0) / 1000, currency)}</div>
            <div className="ob-money-sub">No refunds processed today</div>
          </div>
        </div>
      </section>

      <section className="ob-date-row">
        <div className="ob-date-left">
          <button className="ob-date-arrow" type="button" onClick={goToPreviousDay}>‹</button>
          <button className={`ob-today-pill${isViewingToday ? "" : " is-away"}`} type="button" onClick={goToToday}>Today</button>
          <button className="ob-date-arrow" type="button" onClick={goToNextDay}>›</button>
          <strong>{formatBoardDateLabel(boardDate)}</strong>
          {isViewingToday ? <span>Today</span> : null}
        </div>

        <label className="ob-date-jump">
          <span>Jump to</span>
          <input
            type="date"
            value={boardDate}
            onChange={(event) => goToDate(event.target.value)}
          />
        </label>
      </section>

      <section className="ob-triage-row">
        <div className="ob-triage-card ob-triage-ready">
          <div className="ob-triage-value">{aggregates.ready_total ?? 0}</div>
          <div className="ob-triage-label">✓ Ready</div>
        </div>

        <div className="ob-triage-card ob-triage-checkin">
          <div className="ob-triage-value">{aggregates.check_in_total ?? 0}</div>
          <div className="ob-triage-label">! Needs check-in</div>
        </div>

        <div className="ob-triage-card ob-triage-attention">
          <div className="ob-triage-value">{aggregates.attention_total ?? 0}</div>
          <div className="ob-triage-label">▲ Needs attention</div>
        </div>
      </section>

      <div className="ob-metrics-line">
        <b>{aggregates.total_pax ?? 0}</b> PAX · <b>{aggregates.confirmed_pax_total ?? 0}</b> confirmed · <b>{aggregates.active_hold_pax_total ?? 0}</b> on hold · <b>0</b> unpaid · <b>{aggregates.nitrox_total ?? 0}</b> EAN · <b>{aggregates.rental_gear_total ?? 0}</b> gear · <b>{aggregates.minor_session_count ?? 0}</b> minor · guides:{" "}
        {sessions
          .map((session) => session.metadata?.lead_guide_name)
          .filter(Boolean)
          .filter((name, index, arr) => arr.indexOf(name) === index)
          .join(", ") || "TBD"}
      </div>

      <section className="ob-board">
        {sessions.length === 0 ? (
          <div className="ob-card">No sessions found for today.</div>
        ) : (
          <div className="ob-session-list">
            {sessions.map((session, index) => {
              const summary = summaries[String(session.id)] || {};
              const roster = Array.isArray(summary.roster) ? summary.roster : [];
              const diveCategory = session.dive_category || session.metadata?.dive_category || null;
              const diveCategoryLabel = getDiveCategoryLabel(diveCategory);
              const diveCategorySlug = getDiveCategorySlug(diveCategory);
              const isFirstSession = index === 0;
              const isLastSession = index === sessions.length - 1;
              const isCourseSession = String(session.session_type || "").toLowerCase() === "course";
              const sessionKindClass = isCourseSession ? "ob-session-course" : "ob-session-dive";
              const sessionKindLabel = isCourseSession ? "COURSE" : "DIVE";
              const sessionDetailLine = isCourseSession
                ? `Instructor ${session.metadata?.lead_guide_name || "TBD"} · ${session.metadata?.vessel_name || "Location TBD"}`
                : `${session.metadata?.vessel_name || "Boat"} · Guide: ${session.metadata?.lead_guide_name || "TBD"}`;

              return (
                <article
                  className={`ob-session ${sessionKindClass}${isFirstSession ? " ob-session-day-first" : ""}${isLastSession ? " ob-session-day-last" : ""}`}
                  key={session.id}
                >
                  <div className="ob-session-header">
                    <div className="ob-session-time">
                      <div>{session.start_time || "--:--"}</div>
                      <span>Meet {session.meet_time || "TBD"}</span>
                    </div>

                    <span className={`ob-session-type ${isCourseSession ? "ob-session-type-course" : "ob-session-type-dive"}`}>
                      {sessionKindLabel}
                    </span>

                    <div className="ob-session-main">
                      <h3>
                        {session.title || session.site_name || "Session"}
                        {!isCourseSession && diveCategoryLabel ? (
                          <span className={`ob-category-tag ob-category-${diveCategorySlug}`}>
                            {diveCategoryLabel}
                          </span>
                        ) : null}
                      </h3>
                      <p>{sessionDetailLine}</p>
                    </div>

                    <div className="ob-session-spacer"></div>

                    <div className="ob-session-money">
                      <div>
                        PAX {summary.confirmed_pax ?? 0}
                        {summary.nitrox_requested_count ? ` · EAN ${summary.nitrox_requested_count}` : ""}
                      </div>
                      <strong>{money((summary.confirmed_revenue_minor ?? 0) / 1000, currency)} confirmed</strong>
                      <span>{money((summary.to_collect_minor ?? 0) / 1000, currency)} to collect</span>
                    </div>

                    <div className="ob-session-right">
                      {!summary.session_closed ? (
                        <button
                          type="button"
                          className="ob-manifest-trigger"
                          onClick={() => openSessionManifest(session, roster, summary, isCourseSession)}
                        >
                          {isCourseSession ? "Roster" : "Manifest"}
                        </button>
                      ) : null}

                      {summary.session_closed ? (
                        <span className="cd-closed">
                          <ReadyIcon />
                          {summary.session_closed_by_name
                            ? `Closed · ${summary.session_closed_by_name}`
                            : "Closed"}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="cd-trigger"
                          onClick={() => openCloseDive(session, roster)}
                          disabled={roster.length < 1}
                        >
                          <ReadyIcon />
                          {isCourseSession ? "Close course" : "Close dive"}
                        </button>
                      )}

                      <div className="ob-session-counts">
                        <div>
                          <strong>{summary.confirmed_pax ?? 0}</strong>
                          <span>PAX</span>
                        </div>

                        <div>
                          <strong>{session.capacity_remaining ?? 0}</strong>
                          <span>SPOTS</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="ob-roster">
                    {roster.length === 0 ? (
                      <div className="ob-empty-roster">
                        <span className="ob-empty-wave">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2 11c2-2.6 4-2.6 6 0s4 2.6 6 0 4-2.6 6 0" />
                            <path d="M2 16c2-2.6 4-2.6 6 0s4 2.6 6 0 4-2.6 6 0" />
                          </svg>
                        </span>
                        <span className="ob-empty-text">
                          Open for bookings · <strong>{session.capacity_remaining ?? 0} of {session.capacity_total ?? session.capacity_remaining ?? 0} spots available</strong>
                        </span>
                        <span className="ob-empty-hint">READY TO FILL</span>
                      </div>
                    ) : (
                      <div className="ob-roster-table">
                        <div className="ob-roster-head">
                          <span aria-label="Readiness"></span>
                          <span>Diver</span>
                          <span>Cert</span>
                          <span>Gas</span>
                          <span>Payment</span>
                          <span>Forms</span>
                          <span aria-label="Operational indicators"></span>
                        </div>

                        {roster.map((person, index) => (
                          <div
                            className="ob-roster-row"
                            key={`${person.name || "Diver"}-${index}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => openPeek(person)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                openPeek(person);
                              }
                            }}
                          >
                            <span className={`ob-readiness ob-readiness-${person.readiness || "check_in"}`}>
                              {person.readiness === "ready" ? (
                                <ReadyIcon />
                              ) : person.readiness === "attention" ? (
                                <AttentionIcon />
                              ) : (
                                <CheckInIcon />
                              )}
                            </span>
                            <span className="ob-roster-name">{person.name || "Diver"}</span>
                            <span>{person.cert || "TBD"}</span>
                            <span className={`ob-gas ob-gas-${person.gas === "EAN" ? "ean" : "air"}`}>
                              {person.gas || "Air"}
                            </span>
                            <span className={`ob-payment ob-payment-${person.payment_state || "unpaid"}`}>
                              {formatPaymentState(person.payment_state)}
                            </span>
                            {summary.session_closed ? (
                              person.completion_status === "completed" ? (
                                <span className="cd-demo-rst done">
                                  <span className="cd-mark done"><ReadyIcon /></span>
                                  Completed
                                  {person.completion_acknowledged ? (
                                    <span className="ack confirmed">Confirmed</span>
                                  ) : person.completion_acknowledgment_sent ? (
                                    <span className="ack awaiting">Awaiting</span>
                                  ) : null}
                                </span>
                              ) : person.completion_status === "did_not_dive" ? (
                                <span className="cd-demo-rst no">
                                  <span className="cd-mark no"><DidNotIcon /></span>
                                  Did not dive
                                </span>
                              ) : (
                                <span></span>
                              )
                            ) : (
                              <span className={`ob-forms ob-forms-${person.forms_state || "not_started"}`}>
                                {formatFormsState(person.forms_state)}
                              </span>
                            )}
                            <span className="ob-row-indicators">
                              {person.is_returning ? <span className="ob-flag-returning" title="Returning diver">★</span> : null}
                              {person.has_note ? <span className="ob-flag-note" title="Note">✎</span> : null}
                              {person.has_flag ? <span className="ob-flag-alert" title={person.flag_reason || "Flag"}>▲</span> : null}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <div className="ob-legend">
        <span><span className="ob-readiness ob-readiness-ready"><ReadyIcon /></span> Ready</span>
        <span><span className="ob-readiness ob-readiness-check_in"><CheckInIcon /></span> Needs check-in</span>
        <span><span className="ob-readiness ob-readiness-attention"><AttentionIcon /></span> Needs attention</span>
        <span><span className="ob-gas ob-gas-ean">EAN</span> Nitrox · <span className="ob-gas ob-gas-tx">Tx</span> Trimix · <span className="ob-gas ob-gas-air">Air</span></span>
        <span><span className="ob-flag-returning">★</span> Returning · <span className="ob-flag-note">✎</span> Note · <span className="ob-flag-alert">▲</span> Flag</span>
      </div>

      <div className="ob-hint">
        Tap any diver for the floating record → · Esc / tap outside to close
      </div>

      {peekState ? (
        <div className="ob-peek-layer" onMouseDown={closePeek}>
          <div
            className="peek"
            ref={peekRef}
            role="dialog"
            aria-modal="true"
            aria-label={`${peekState.person.name || "Diver"} record`}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="pk-crown">
              <button className="pk-close" aria-label="Close" type="button" onClick={closePeek}>×</button>
              <div className="pk-id">
                <div className="pk-avatar">{getPeekInitials(peekState.person.name)}</div>
                <div>
                  <div className="pk-name">{peekState.person.name || "Diver"}</div>
                  <div className="pk-meta">{peekState.person.cert || "Cert TBD"}</div>
                  <div className="pk-tags">
                    <span className={`tag ${getPeekReadinessClass(peekState.person.readiness)}`}>
                      {getPeekReadinessLabel(peekState.person.readiness)}
                    </span>
                    <span className={`tag ${peekState.person.is_returning ? "ret" : "newc"}`}>
                      {peekState.person.is_returning ? "★ Returning" : "New customer"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="pk-scroll">
              <div className={`reassure ${getPeekReadinessClass(peekState.person.readiness)}`}>
                <span className="rz-ic">{getPeekReadinessIcon(peekState.person.readiness)}</span>
                <span>{getPeekReassureText(peekState.person)}</span>
              </div>
              <div className="pk-sec-label">Dive boarding-pass</div>
              <div className="tiles">
                <div className={`tile ${peekState.person.payment_state === "paid" ? "good" : ""}`}>
                  <div className="tl">{PayIcon()}<span>Payment</span></div>
                  <div className="tv">
                    {formatPaymentState(peekState.person.payment_state)}
                    <small>{getPeekMoney(peekState.person, currency)}</small>
                  </div>
                </div>
                <div className="tile">
                  <div className="tl">{GasIcon()}<span>Gas</span></div>
                  <div className="tv">
                    {getPeekGasLabel(peekState.person.gas)}
                    <small>{getPeekGasDetail(peekState.person)}</small>
                  </div>
                </div>
                <div className="tile">
                  <div className="tl">{CertIcon()}<span>Certification</span></div>
                  <div className="tv">
                    {peekState.person.cert || "Cert TBD"}
                    <small>{peekState.person.cert ? "Projected from roster" : "Confirm at check-in"}</small>
                  </div>
                </div>
                <div className={`tile ${peekState.person.forms_state === "complete" ? "good" : ""}`} role="button" tabIndex={0} onClick={() => beginPeekMode("forms", peekState.person)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); beginPeekMode("forms", peekState.person); } }}>
                  <div className="tl">{FormsIcon()}<span>Forms</span></div>
                  <div className="tv">
                    {formatFormsState(peekState.person.forms_state)}
                    <small>{peekState.person.forms_state === "complete" ? "Signed" : "Awaiting signature"}</small>
                  </div>
                </div>
                <div className="tile span2">
                  <div className="tl">{GearIcon()}<span>Gear</span></div>
                  <div className="tv">
                    {getPeekGearLabel(peekState.person)}
                    <small>{getPeekGearDetail(peekState.person)}</small>
                  </div>
                </div>
              </div>
              <div className="pk-actions">
                {peekMode === "summary" ? (
                  <>
                    <div className="pk-act-label">Clear the blocker — stay on the board</div>
                    <div className="pk-act-row">
                      <div className="pk-act" role="button" tabIndex={0} aria-label="Add note" onClick={() => beginPeekMode("note", peekState.person)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); beginPeekMode("note", peekState.person); } }}>
                        <span className="ai"><NoteIcon /></span>
                        <span className="at">Add note<small>care · request</small></span>
                      </div>
                      <div className="pk-act" role="button" tabIndex={0} aria-label="Update cert" onClick={() => beginPeekMode("cert", peekState.person)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); beginPeekMode("cert", peekState.person); } }}>
                        <span className="ai"><CertIcon /></span>
                        <span className="at">Update cert<small>verify card</small></span>
                      </div>
                      <div className="pk-act restricted" role="button" tabIndex={0} aria-label="Update medical" onClick={() => beginPeekMode("medical", peekState.person)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); beginPeekMode("medical", peekState.person); } }}>
                        <span className="ai"><MedicalIcon /></span>
                        <span className="at">Update medical<small>restricted</small></span>
                      </div>
                    </div>
                  </>
                ) : null}

                {peekMode === "note" ? (
                  <div className="pk-editor">
                    <div className="pk-act-label">Add operator note</div>
                    <textarea value={peekNoteBody} onChange={(event) => { setPeekNoteBody(event.target.value); setPeekMessage(""); }} placeholder="Add an operator-only note for this diver..." />
                    {peekMessage ? <div className="pk-msg">{peekMessage}</div> : null}
                    <div className="pk-edit-actions">
                      <button type="button" className="pk-cancel" onClick={() => setPeekMode("summary")} disabled={peekSaving}>Cancel</button>
                      <button type="button" className="pk-save" onClick={savePeekNote} disabled={peekSaving}>{peekSaving ? "Saving" : "Save note"}</button>
                    </div>
                  </div>
                ) : null}

                {peekMode === "cert" ? (
                  <div className="pk-editor">
                    <div className="pk-act-label">Update certification</div>
                    <label>Agency<input value={peekCertAgency} onChange={(event) => { setPeekCertAgency(event.target.value); setPeekMessage(""); }} placeholder="PADI, SSI, RAID..." /></label>
                    <label>Level<input value={peekCertLevel} onChange={(event) => { setPeekCertLevel(event.target.value); setPeekMessage(""); }} placeholder="Open Water Diver" /></label>
                    <label>Certification number<input value={peekCertNumber} onChange={(event) => { setPeekCertNumber(event.target.value); setPeekMessage(""); }} /></label>
                    <label>Status
                      <select value={peekCertStatus} onChange={(event) => { setPeekCertStatus(event.target.value); setPeekMessage(""); }}>
                        <option value="not_recorded">Not recorded</option>
                        <option value="claimed">Claimed</option>
                        <option value="verified">Verified</option>
                        <option value="needs_review">Needs review</option>
                      </select>
                    </label>
                    {peekMessage ? <div className="pk-msg">{peekMessage}</div> : null}
                    <div className="pk-edit-actions">
                      <button type="button" className="pk-cancel" onClick={() => setPeekMode("summary")} disabled={peekSaving}>Cancel</button>
                      <button type="button" className="pk-save" onClick={savePeekCert} disabled={peekSaving}>{peekSaving ? "Saving" : "Save certification"}</button>
                    </div>
                  </div>
                ) : null}

                {peekMode === "medical" ? (
                  <div className="pk-editor">
                    <div className="pk-act-label">Update readiness</div>
                    <label>Medical status
                      <select value={peekMedicalStatus} onChange={(event) => { setPeekMedicalStatus(event.target.value); setPeekMessage(""); }}>
                        <option value="not_collected">Not collected</option>
                        <option value="no_conditions_declared">No conditions declared</option>
                        <option value="declared_cleared_by_physician">Declared — cleared by physician</option>
                        <option value="declared_awaiting_clearance">Declared — awaiting clearance</option>
                        <option value="needs_review">Needs review</option>
                      </select>
                    </label>
                    <label>Customer status
                      <select value={peekCustomerStatus} onChange={(event) => { setPeekCustomerStatus(event.target.value); setPeekMessage(""); }}>
                        <option value="active">Active</option>
                        <option value="watch">Watch</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </label>
                    <label>Readiness note<textarea value={peekReadinessNote} onChange={(event) => { setPeekReadinessNote(event.target.value); setPeekMessage(""); }} placeholder="Optional operator-only readiness note..." /></label>
                    {peekMessage ? <div className="pk-msg">{peekMessage}</div> : null}
                    <div className="pk-edit-actions">
                      <button type="button" className="pk-cancel" onClick={() => setPeekMode("summary")} disabled={peekSaving}>Cancel</button>
                      <button type="button" className="pk-save" onClick={savePeekMedical} disabled={peekSaving}>{peekSaving ? "Saving" : "Save readiness"}</button>
                    </div>
                  </div>
                ) : null}

                {peekMode === "forms" ? (
                  <div className="pk-editor">
                    <div className="pk-act-label">Approve forms</div>
                    <label className="pk-check"><input type="checkbox" checked={peekLiabilitySigned} onChange={(event) => { setPeekLiabilitySigned(event.target.checked); setPeekMessage(""); }} /> Liability release signed</label>
                    <label className="pk-check"><input type="checkbox" checked={peekMedicalSigned} onChange={(event) => { setPeekMedicalSigned(event.target.checked); setPeekMessage(""); }} /> Medical declaration received</label>
                    {peekState.person.is_minor === true ? (
                      <label className="pk-check"><input type="checkbox" checked={peekGuardianSigned} onChange={(event) => { setPeekGuardianSigned(event.target.checked); setPeekMessage(""); }} /> Guardian / responsible adult signed</label>
                    ) : null}
                    {peekMessage ? <div className="pk-msg">{peekMessage}</div> : null}
                    <div className="pk-edit-actions">
                      <button type="button" className="pk-cancel" onClick={() => setPeekMode("summary")} disabled={peekSaving}>Cancel</button>
                      <button type="button" className="pk-save" onClick={savePeekForms} disabled={peekSaving}>{peekSaving ? "Saving" : "Approve forms"}</button>
                    </div>
                  </div>
                ) : null}

                <div className="pk-act-full" role="button" tabIndex={0} onClick={() => openCustomerRecord(peekState.person)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openCustomerRecord(peekState.person); } }}>
                  <UserIcon /> Open customer record →
                </div>
                <div className="pk-act-cap">Fast clear happens here. Full record opens only when you ask for it.</div>
              </div>
            </div>
            <div className="pk-foot">
              <div className="priv">Medical detail is never shown — only "clearance required." Money actions live in Bookings; the board routes you there. Authenticated staff only.</div>
            </div>
          </div>



      
        </div>
      ) : null}

{manifestPreview ? (
        <div className="mf-overlay" onMouseDown={closeManifestPreview}>
          <div className="mf-card mf-generator" onMouseDown={(event) => event.stopPropagation()}>
            <div className="mf-head">
              <button
                className="mf-close"
                aria-label="Close"
                type="button"
                onClick={closeManifestPreview}
                disabled={manifestGenerating}
              >
                ×
              </button>
              <div className="mf-title">{manifestPreview.title}</div>
              <div className="mf-sub">{manifestPreview.subtitle}</div>
            </div>

            <div className={`mf-body${manifestCorrectionPerson ? " has-correction" : ""}`}>
              <div className="mf-toolbar">
                <label className="mf-format-select">
                  <span>Manifest format</span>
                  <select
                    value={manifestProfileCode}
                    onChange={(event) => {
                      const profileCode =
                        event.target.value as ManifestProfileCode;

                      setManifestProfileCode(profileCode);

                      void loadManifestGrid(
                        manifestPreview.scope,
                        profileCode
                      );
                    }}
                    disabled={
                      manifestGenerating
                      || manifestGridLoading
                    }
                  >
                    {MANIFEST_PROFILES.map((profile) => (
                      <option
                        key={profile.code}
                        value={profile.code}
                      >
                        {profile.title}
                      </option>
                    ))}
                  </select>
                </label>

                {MANIFEST_PROFILES
                  .filter(
                    (profile) =>
                      profile.code === manifestProfileCode
                  )
                  .map((profile) => (
                    <div
                      className="mf-format-status"
                      key={profile.code}
                    >
                      <span className={`mf-authority-state${profile.authorityReady ? " is-ready" : " is-provisional"}`}>
                        {profile.authorityReady
                          ? "Official format"
                          : "Provisional format"}
                      </span>
                      <small>{profile.description}</small>
                    </div>
                  ))}
              </div>

              <div className="mf-readiness-bar">
                <div>
                  <span>Scope</span>
                  <strong>
                    {manifestPreview.mode === "day"
                      ? "Full day · deduplicated"
                      : "Single event"}
                  </strong>
                </div>

                <div>
                  <span>Practitioners</span>
                  <strong>
                    {manifestGrid?.readiness?.total
                      ?? manifestGrid?.roster?.length
                      ?? 0}
                  </strong>
                </div>

                <div>
                  <span>Identity complete</span>
                  <strong>
                    {manifestGrid?.readiness?.complete ?? 0}
                    {" / "}
                    {manifestGrid?.readiness?.total
                      ?? manifestGrid?.roster?.length
                      ?? 0}
                  </strong>
                </div>

                <div className={
                  Number(
                    manifestGrid?.readiness?.blocking || 0
                  ) > 0
                    ? "is-blocking"
                    : "is-complete"
                }>
                  <span>Readiness</span>
                  <strong>
                    {Number(
                      manifestGrid?.readiness?.blocking || 0
                    ) > 0
                      ? `${manifestGrid?.readiness?.blocking} blocking`
                      : "Ready"}
                  </strong>
                </div>
              </div>

              {manifestGridLoading ? (
                <div className="mf-grid-state">
                  Loading official Manifest data...
                </div>
              ) : manifestError ? (
                <div
                  className="mf-generation-error"
                  role="alert"
                >
                  {manifestError}
                </div>
              ) : !manifestGrid?.roster?.length ? (
                <div className="mf-grid-state">
                  No practitioners found for this scope.
                </div>
              ) : (
                manifestViewMode === "preview" ? (
                  <div className="mf-preview-wrap">
                    <table
                      className="mf-preview-grid"
                      aria-label="Official Manifest preview"
                    >
                      <colgroup>
                        <col className="mf-col-first" />
                        <col className="mf-col-last" />
                        <col className="mf-col-dob" />
                        <col className="mf-col-nationality" />
                        <col className="mf-col-gender" />
                        <col className="mf-col-passport" />
                        <col className="mf-col-activity" />
                        <col className="mf-col-stay" />
                        <col className="mf-col-room" />
                        <col className="mf-col-responsible-passport" />
                        <col className="mf-col-responsible-nationality" />
                      </colgroup>

                      <thead>
                        <tr>
                          <th>First name</th>
                          <th>Last name</th>
                          <th>Birthdate</th>
                          <th>Nationality</th>
                          <th>Gender</th>
                          <th>Passport number</th>
                          <th>Activity</th>
                          <th>Accommodation</th>
                          <th>Room number</th>
                          <th>Responsible passport</th>
                          <th>Responsible nationality</th>
                        </tr>
                      </thead>

                      <tbody>
                        {manifestGrid.roster.map(
                          (person, index) => {
                            const previewValue = (
                              value: unknown,
                              editable: boolean,
                              notRequired = false
                            ) => {
                              if (!manifestValueMissing(value)) {
                                return String(value);
                              }

                              if (notRequired) {
                                return (
                                  <span className="mf-preview-na">
                                    Not required
                                  </span>
                                );
                              }

                              if (editable) {
                                return (
                                  <button
                                    type="button"
                                    className="mf-preview-add"
                                    onClick={() => {
                                      setManifestViewMode("edit");
                                      beginManifestCorrection(
                                        person
                                      );
                                    }}
                                    disabled={
                                      manifestCorrectionSaving
                                    }
                                  >
                                    Add
                                  </button>
                                );
                              }

                              return (
                                <span className="mf-preview-missing">
                                  Missing
                                </span>
                              );
                            };

                            return (
                              <tr
                                key={
                                  person.participant_id
                                  ?? `manifest-preview-${index}`
                                }
                              >
                                <td>
                                  {previewValue(person.first, false)}
                                </td>
                                <td>
                                  {previewValue(person.last, false)}
                                </td>
                                <td>
                                  {previewValue(person.dob, true)}
                                </td>
                                <td>
                                  {previewValue(
                                    person.nationality,
                                    true
                                  )}
                                </td>
                                <td>
                                  {previewValue(person.gender, true)}
                                </td>
                                <td>
                                  {previewValue(
                                    person.passport,
                                    true
                                  )}
                                </td>
                                <td>
                                  {previewValue(
                                    person.activity,
                                    false
                                  )}
                                </td>
                                <td>
                                  {previewValue(
                                    person.accommodation,
                                    true
                                  )}
                                </td>
                                <td>
                                  {previewValue(
                                    person.roomNumber,
                                    true
                                  )}
                                </td>
                                <td>
                                  {previewValue(
                                    person.isMinor
                                      ? person.guardianPassport
                                      : null,
                                    Boolean(person.isMinor),
                                    !person.isMinor
                                  )}
                                </td>
                                <td>
                                  {previewValue(
                                    person.isMinor
                                      ? person.guardianNationality
                                      : null,
                                    Boolean(person.isMinor),
                                    !person.isMinor
                                  )}
                                </td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                <div className="mf-roster-list">
                  {manifestGrid.roster.map(
                    (person, index) => {
                      const correctionFields =
                        correctionFieldsForPerson(person);
                      const participantName = [
                        person.first,
                        person.last
                      ]
                        .filter(Boolean)
                        .join(" ")
                        || "Participant";

                      const renderManifestValue = (
                        value: unknown,
                        editable: boolean
                      ) => {
                        const missing =
                          manifestValueMissing(value);

                        if (!missing) {
                          return (
                            <span className="mf-detail-value">
                              {String(value)}
                            </span>
                          );
                        }

                        if (editable) {
                          return (
                            <button
                              className="mf-add-value"
                              type="button"
                              onClick={() =>
                                beginManifestCorrection(person)
                              }
                              disabled={
                                manifestCorrectionSaving
                              }
                            >
                              Add
                            </button>
                          );
                        }

                        return (
                          <span className="mf-not-required">
                            {person.isMinor
                              ? "Missing"
                              : "Not required"}
                          </span>
                        );
                      };

                      return (
                        <article
                          className={
                            `mf-person-row${
                              manifestCorrectionParticipantId
                              === Number(person.participant_id)
                                ? " is-selected"
                                : ""
                            }`
                          }
                          key={
                            person.participant_id
                            ?? `manifest-row-${index}`
                          }
                        >
                          <div className="mf-person-primary">
                            <div>
                              <span className="mf-detail-label">
                                Participant
                              </span>
                              <strong>{participantName}</strong>
                              {person.isMinor ? (
                                <span className="mf-minor-state">
                                  Minor
                                </span>
                              ) : null}
                            </div>

                            <div className="mf-person-action">
                              {correctionFields.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    beginManifestCorrection(person)
                                  }
                                  disabled={
                                    manifestCorrectionSaving
                                  }
                                >
                                  Complete
                                </button>
                              ) : (
                                <span className="mf-complete-state">
                                  Complete
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="mf-person-details">
                            <div className="mf-detail-group">
                              <span className="mf-group-title">
                                Identity
                              </span>

                              <div className="mf-detail-items">
                                <div>
                                  <span className="mf-detail-label">
                                    Birthdate
                                  </span>
                                  {renderManifestValue(
                                    person.dob,
                                    manifestValueMissing(person.dob)
                                  )}
                                </div>

                                <div>
                                  <span className="mf-detail-label">
                                    Nationality
                                  </span>
                                  {renderManifestValue(
                                    person.nationality,
                                    manifestValueMissing(
                                      person.nationality
                                    )
                                  )}
                                </div>

                                <div>
                                  <span className="mf-detail-label">
                                    Gender
                                  </span>
                                  {renderManifestValue(
                                    person.gender,
                                    manifestValueMissing(
                                      person.gender
                                    )
                                  )}
                                </div>

                                <div>
                                  <span className="mf-detail-label">
                                    Passport
                                  </span>
                                  {renderManifestValue(
                                    person.passport,
                                    manifestValueMissing(
                                      person.passport
                                    )
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="mf-detail-group">
                              <span className="mf-group-title">
                                Activity
                              </span>

                              <div className="mf-detail-items">
                                <div>
                                  <span className="mf-detail-label">
                                    Activity
                                  </span>
                                  {renderManifestValue(
                                    person.activity,
                                    false
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="mf-detail-group">
                              <span className="mf-group-title">
                                Stay
                              </span>

                              <div className="mf-detail-items">
                                <div>
                                  <span className="mf-detail-label">
                                    Accommodation
                                  </span>
                                  {renderManifestValue(
                                    person.accommodation,
                                    manifestValueMissing(
                                      person.accommodation
                                    )
                                  )}
                                </div>

                                <div>
                                  <span className="mf-detail-label">
                                    Room
                                  </span>
                                  {renderManifestValue(
                                    person.roomNumber,
                                    manifestValueMissing(
                                      person.roomNumber
                                    )
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="mf-detail-group mf-responsible-group">
                              <span className="mf-group-title">
                                Responsible person
                              </span>

                              {person.isMinor ? (
                                <div className="mf-detail-items">
                                  <div>
                                    <span className="mf-detail-label">
                                      Passport
                                    </span>
                                    {renderManifestValue(
                                      person.guardianPassport,
                                      manifestValueMissing(
                                        person.guardianPassport
                                      )
                                    )}
                                  </div>

                                  <div>
                                    <span className="mf-detail-label">
                                      Nationality
                                    </span>
                                    {renderManifestValue(
                                      person.guardianNationality,
                                      manifestValueMissing(
                                        person.guardianNationality
                                      )
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="mf-adult-state">
                                  <strong>Adult</strong>
                                  <span>Not required</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </article>
                      );
                    }
                  )}
                </div>
                )
              )}

              {manifestViewMode === "edit"
              && manifestCorrectionPerson ? (
                <section
                  className="mf-correction-dock"
                  aria-label="Complete Manifest details"
                >
                  <div className="mf-correction-head">
                    <div>
                      <strong>
                        Complete Manifest details
                      </strong>
                      <span>
                        {[
                          manifestCorrectionPerson.first,
                          manifestCorrectionPerson.last
                        ]
                          .filter(Boolean)
                          .join(" ")
                          || "Participant"}
                      </span>
                    </div>

                    <div className="mf-correction-head-actions">
                      <button
                        type="button"
                        className="mf-correction-cancel"
                        onClick={cancelManifestCorrection}
                        disabled={manifestCorrectionSaving}
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        className="mf-correction-save"
                        onClick={() =>
                          saveManifestCorrection(
                            manifestCorrectionPerson
                          )
                        }
                        disabled={manifestCorrectionSaving}
                      >
                        {manifestCorrectionSaving
                          ? "Saving..."
                          : "Save updates"}
                      </button>
                    </div>
                  </div>

                  <div className="mf-correction-fields">
                    {correctionFieldsForPerson(
                      manifestCorrectionPerson
                    ).map((field) => (
                      <label key={field.field}>
                        <span>{field.label}</span>

                        {field.type === "nationality" ? (
                          <select
                            value={
                              manifestCorrectionDraft[
                                field.field
                              ] || ""
                            }
                            onChange={(event) =>
                              setManifestCorrectionDraft(
                                (current) => ({
                                  ...current,
                                  [field.field]:
                                    event.target.value
                                })
                              )
                            }
                            disabled={manifestCorrectionSaving}
                          >
                            <option value="">Select</option>
                            {NATIONALITIES.map(
                              (nationality) => (
                                <option
                                  key={nationality}
                                  value={nationality}
                                >
                                  {nationality}
                                </option>
                              )
                            )}
                          </select>
                        ) : field.type === "gender" ? (
                          <select
                            value={
                              manifestCorrectionDraft[
                                field.field
                              ] || ""
                            }
                            onChange={(event) =>
                              setManifestCorrectionDraft(
                                (current) => ({
                                  ...current,
                                  [field.field]:
                                    event.target.value
                                })
                              )
                            }
                            disabled={manifestCorrectionSaving}
                          >
                            <option value="">Select</option>
                            <option value="Male">Male</option>
                            <option value="Female">
                              Female
                            </option>
                          </select>
                        ) : (
                          <input
                            type={field.type}
                            value={
                              manifestCorrectionDraft[
                                field.field
                              ] || ""
                            }
                            onChange={(event) =>
                              setManifestCorrectionDraft(
                                (current) => ({
                                  ...current,
                                  [field.field]:
                                    event.target.value
                                })
                              )
                            }
                            disabled={manifestCorrectionSaving}
                          />
                        )}
                      </label>
                    ))}
                  </div>

                  {manifestCorrectionError ? (
                    <div
                      className="mf-correction-error"
                      role="alert"
                    >
                      {manifestCorrectionError}
                    </div>
                  ) : null}
                </section>
              ) : null}
            </div>

            <div className="mf-foot">
              <button
                className="mf-done"
                type="button"
                onClick={closeManifestPreview}
                disabled={
                  manifestGenerating
                  || manifestCorrectionSaving
                }
              >
                {manifestViewMode === "preview"
                  ? "Cancel"
                  : "Close"}
              </button>

              <button
                className="mf-mode-toggle"
                type="button"
                onClick={() => {
                  if (manifestViewMode === "edit") {
                    cancelManifestCorrection();
                    setManifestViewMode("preview");
                  } else {
                    setManifestViewMode("edit");
                  }
                }}
                disabled={
                  manifestGenerating
                  || manifestCorrectionSaving
                }
              >
                {manifestViewMode === "preview"
                  ? "Edit details"
                  : "Preview"}
              </button>
              <button
                className="mf-print"
                type="button"
                onClick={generateManifestWorkbook}
                disabled={manifestGenerating}
              >
                {manifestGenerating
                  ? "Generating..."
                  : "Generate workbook"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {closeDiveState ? (
        <div className="cd-overlay" onMouseDown={closeCloseDive}>
          <div className="cd-card" onMouseDown={(event) => event.stopPropagation()}>
            <div className="cd-head">
              <button className="cd-close" aria-label="Close" type="button" onClick={closeCloseDive}>×</button>
              <div className="cd-title">Close dive</div>
              <div className="cd-sub">
                {closeDiveState.session.title || closeDiveState.session.site_name || "Session"} · {closeDiveState.session.session_type || "Dive"} · {closeDiveState.session.start_time || "--:--"} · today
              </div>
            </div>

            <div className="cd-body">
              <p className="cd-q">Did this dive complete?</p>

              {closeDiveState.roster.map((person, index) => {
                const participantId = Number(person.participant_id);
                const isCompleted = closeDiveState.completedParticipantIds.includes(participantId);

                return (
                  <div className="cd-row" key={`${participantId || person.name || "participant"}-${index}`}>
                    <span className="cd-av">{getPeekInitials(person.name)}</span>
                    <span className="cd-nm">{person.name || "Diver"}</span>
                    <span className="cd-toggle">
                      <button
                        className={`cd-seg dived${isCompleted ? " on" : ""}`}
                        type="button"
                        onClick={() => toggleCloseDiveParticipant(participantId)}
                        disabled={closeDiveState.isSubmitting}
                      >
                        {isCompleted ? <ReadyIcon /> : null}
                        Dived
                      </button>
                      <button
                        className={`cd-seg didnot${isCompleted ? "" : " on"}`}
                        type="button"
                        onClick={() => toggleCloseDiveParticipant(participantId)}
                        disabled={closeDiveState.isSubmitting}
                      >
                        {isCompleted ? null : <AttentionIcon />}
                        Did not
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="cd-foot">
              <button className="cd-confirm" type="button" onClick={submitCloseDive} disabled={closeDiveState.isSubmitting}>
                <ReadyIcon />
                Confirm — {closeDiveState.completedParticipantIds.length} completed, {closeDiveState.roster.length - closeDiveState.completedParticipantIds.length} did not dive
              </button>
              <button
                className="cd-didnothappen"
                type="button"
                onClick={markCloseDiveAllDidNotDive}
                disabled={closeDiveState.isSubmitting}
              >
                Dive did not happen — close all as not completed
              </button>
              {closeDiveState.error ? (
                <div className="cd-error">{closeDiveState.error}</div>
              ) : null}
              <p className="cd-attest">Closing as authenticated staff · just now · authenticated staff only</p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
